import React, { useEffect, useState, useMemo } from 'react';
import validator from 'validator';
import { Typography } from '../typography';
import { Button } from '../button';
import { Input, InputPrice } from '../input';
import { enqueueSnackbar } from '../snackbar';
import {
  ModalBase,
  ModalWrapper,
  ModalFooterButtons,
  ModalBodyCustomColumns,
} from './ModalComponents';
import { useEditManageSalesMutation } from '../../hooks';
import { INPUT_ERRORS, MESSAGES } from '../../const';
import { ContainerType, FeeType } from '../../types';
import { CheckboxLabel } from '../checkbox';
import { calculatePriceWithFee, formatContainerType } from '../../utils';
import { i18n } from '../../i18n';

interface ManageSalesEditModalType {
  data: {
    id: string;
    number: string;
    price: number;
    type: ContainerType;
    allowCounterOffers: boolean;
    description: string;

    feeType: FeeType;
    feeValue: number;
  }[];
  isSubmitClicked: boolean;
  submitClick: () => void;
  open: () => void;
}

export const managesalesEditModal: ManageSalesEditModalType = {
  isSubmitClicked: false,
  submitClick: () => null,
  open: () => null,
  data: [],
};

export const ManageSalesEditModal = () => {
  const { mutate } = useEditManageSalesMutation();
  const [open, setOpen] = useState(false);
  const [isSamePrice, setIsSamePrice] = useState(false);
  const [price, setPrice] = useState([0]);
  const [allowCounterOffers, setAllowCounterOffers] = useState(false);
  const [description, setDescription] = useState('');
  const total = useMemo(() => {
    return price.reduce(
      (acc, val, i) =>
        acc +
        calculatePriceWithFee(val, {
          type: managesalesEditModal.data[i]?.feeType,
          value: managesalesEditModal.data[i]?.feeValue,
        }),
      0,
    );
  }, [price, managesalesEditModal.data]);

  const [, setUpdate] = useState<any>();

  const validators = {
    description: description
      ? !validator.isLength(description, { max: 500 })
        ? INPUT_ERRORS.NOT_VALID.SALE_DESCRIPTION_LENGTH
        : ''
      : '',
  };

  useEffect(() => {
    managesalesEditModal.open = () => {
      managesalesEditModal.isSubmitClicked = false;
      setPrice(managesalesEditModal.data.map((item) => item.price));
      setAllowCounterOffers(false);
      setDescription('');
      setOpen(true);
    };
  }, []);

  const handleOnClose = () => {
    managesalesEditModal.data = [];
    setOpen(!open);
  };

  const handlePriceChange = (e: any, i: number) => {
    if (isSamePrice) {
      const newPrice = Array(price.length).fill(Number(e.target.value));
      setPrice(newPrice);

      return;
    }

    const newPrice = [...price];
    newPrice[i] = Number(e.target.value);
    setPrice(newPrice);
  };

  const handleSubmitClick = async () => {
    managesalesEditModal.isSubmitClicked = true;
    setUpdate(Math.random());

    if ([...Object.values(validators).map((val) => !val)].some((val) => !val)) {
      return;
    }

    managesalesEditModal.isSubmitClicked = false;

    mutate(
      managesalesEditModal.data.map((item, i) => ({
        id: item.id,
        ownerSetPrice: price[i],
        allowCounterOffers,
        description,
      })),
      {
        onSuccess: () => {
          setOpen(false);
          managesalesEditModal.submitClick();
        },
        onError: () => {
          enqueueSnackbar(MESSAGES.MANAGE_SALES.ERROR.NOT_EDITED, 'error');
        },
      },
    );
  };

  return (
    <ModalBase
      id={'managesales-edit-modal'}
      open={open}
      onClose={handleOnClose}
      paperSize={'very-big'}
    >
      <ModalWrapper>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.modal.manageSalesEditTitle', { total })}$
        </Typography>
        <ModalBodyCustomColumns columns={'1fr 4fr 0fr 0fr 0fr'} withInputHeight>
          {isSamePrice && (
            <InputPrice
              data-size={'medium'}
              value={price[0]}
              onChange={(e) => handlePriceChange(e, 0)}
            />
          )}
          <CheckboxLabel
            label={i18n.t('label.samePriceForAll')}
            checked={isSamePrice}
            onChange={(e) => setIsSamePrice(e.target.checked)}
          />
        </ModalBodyCustomColumns>
        <ModalBodyCustomColumns columns={'1fr 1fr 1fr 1fr 1fr'} isSticky>
          <Typography variant={'textS'}>{i18n.t('label.containerNumber')}</Typography>
          <Typography variant={'textS'}>{i18n.t('label.containerType')}</Typography>
          <Typography variant={'textS'}>{i18n.t('label.price')}*</Typography>
          <Typography variant={'textS'}>{i18n.t('label.fee')}</Typography>
          <Typography variant={'textS'}>{i18n.t('label.finalPrice')}</Typography>
        </ModalBodyCustomColumns>
        {managesalesEditModal.data.map((item, i) => (
          <React.Fragment key={i}>
            <ModalBodyCustomColumns columns={'1fr 1fr 1fr 1fr 1fr'}>
              <Input data-size={'medium'} defaultValue={item.number} readOnly />
              <Input data-size={'medium'} defaultValue={formatContainerType(item.type)} readOnly />
              <InputPrice
                data-size={'medium'}
                value={price[i]}
                onChange={(e) => handlePriceChange(e, i)}
              />
              <InputPrice
                data-size={'medium'}
                value={item.feeValue}
                readOnly
                isPercent={item.feeType === FeeType.PERCENTAGE}
              />
              <InputPrice
                data-size={'medium'}
                value={calculatePriceWithFee(price[i], {
                  type: item.feeType,
                  value: item.feeValue,
                })}
                readOnly
              />
            </ModalBodyCustomColumns>
            {/* <Input
          label={i18n.t('label.description')}
          placeholder={''}
          data-size={'medium'}
          rows={4}
          multiline
          defaultValue={description}
          onChange={(e) => setDescription(e.target.value)}
          error={!!validators.description}
          helperText={validators.description}
        /> */}
            {/* <CheckboxLabel
          label="Allow Counter Offers"
          checked={allowCounterOffers}
          onChange={(e) => setAllowCounterOffers(e.target.checked)}
        /> */}
          </React.Fragment>
        ))}
        <Typography variant={'textM'}>{i18n.t('pages.modal.manageSalesEditHelperText')}</Typography>
        <ModalFooterButtons>
          <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
            {i18n.t('button.cancel')}
          </Button>
          <Button
            size={'medium'}
            data-color={'primary'}
            onClick={handleSubmitClick}
            disabled={!!validators.description}
          >
            {i18n.t('button.edit')}
          </Button>
        </ModalFooterButtons>
      </ModalWrapper>
    </ModalBase>
  );
};
